import React, { useEffect, useMemo, useState } from "react";
import { Button, Popover } from "antd";
import type { SelectProps } from "antd";
import dayjs from "dayjs";
import Icon from "@mdi/react";
import { mdiAirplane } from "@mdi/js";
import { useNavigate, useSearchParams } from "react-router-dom";
import ListApi from "../../../../api-services/list-api.service";
import type { SearchOption } from "../../../../api-services/list-api.service";
import { monthFormat } from "../../../../utils/antd.props";
import useBreakpoint from "../../../../utils/useBreakpoint";
import {
  EMPTY_FILTERS,
  ListingFilterState,
  parseListingParams,
  toQueryString,
} from "../../../../utils/listingSearchParams";
import type { TravelMode } from "../Homepage";
import { HomeIcon, PinIcon } from "../search-icons";
import BrandDropdown from "./BrandDropdown";
import { BrandOption } from "./BrandCards";
import BrandPickerSheet from "./BrandPickerSheet";
import MonthGrid from "./MonthGrid";
import MonthPickerSheet from "./MonthPickerSheet";
import OptionPickerSheet from "./OptionPickerSheet";
import PickerList, { PickerGroup, PickerOption, labelForValue } from "./PickerList";

const ICONS = "/iceholidays-assets/images/icons";
const listApi = new ListApi();
const POPULAR = ["Japan", "Korea", "China", "Switzerland", "Europe"];

interface Props {
  mode: TravelMode;
  onModeChange: (mode: TravelMode) => void;
}

const renderItem = ({ id, name }: SearchOption) => ({
  value: name,
  label: name,
});

const modeToSearchType = (mode: TravelMode) => mode === "cruise" ? "cruise" : "series";
type OpenField = "keyword" | "exEnd" | "date" | "brands" | null;
type SheetField = "destination" | "departure" | "date" | "brands" | null;

// Hero banner + search bar for the redesigned landing page (v2). Uses the
// listing query contract so searches land on the redesigned listing switcher.
export default function HeroSearch({ mode, onModeChange }: Props) {
  const navigate = useNavigate();
  const isMobile = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [destinations, setDestinations] = useState<SelectProps["options"]>([]);
  const [exEnds, setExEnds] = useState<SelectProps["options"]>([]);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [open, setOpen] = useState<OpenField>(null);
  const [sheet, setSheet] = useState<SheetField>(null);
  const [keywordText, setKeywordText] = useState("");
  const [dateYear, setDateYear] = useState(dayjs().year());
  const [draft, setDraft] = useState<ListingFilterState>(() => ({
    ...EMPTY_FILTERS,
    ...parseListingParams(searchParams)
  }));

  useEffect(() => {
    setKeywordText(draft.keyword);
    if (draft.date) setDateYear(dayjs(draft.date).year());
  }, []);

  useEffect(() => {
    let active = true;
    // cruise_country_list has no city data; country_list carries both.
    const locationsPromise = mode === "cruise"
      ? listApi.getCruiseLocations().then((countries) => ({ countries, cities: [] as SearchOption[] }))
      : listApi.getCountryList();
    const itinerariesPromise = mode === "cruise" ? listApi.getCruiseItineraries() : listApi.getItineraries();

    Promise.all([locationsPromise, itinerariesPromise])
      .then(([{ countries, cities }, itineraries]) => {
        if (!active) return;
        const options: SelectProps["options"] = [];

        if (countries.length > 0) {
          options.push({
            label: "Destinations",
            options: countries.map((location: SearchOption) => renderItem(location))
          });
        }

        // A city sharing its name with a country (e.g. Singapore) is already
        // listed under Destinations and searches the same way, so skip it.
        const countryNames = new Set(countries.map(({ name }) => name.toLowerCase()));
        const cityOptions = cities.filter(({ name }) => !countryNames.has(name.toLowerCase()));

        if (cityOptions.length > 0) {
          options.push({
            label: "Cities",
            options: cityOptions.map((city: SearchOption) => renderItem(city))
          });
        }

        if (itineraries.length > 0) {
          options.push({
            label: "Itineraries",
            options: itineraries.map((itinerary: SearchOption) => renderItem(itinerary))
          });
        }

        setDestinations(options);
      })
      .catch(() => {
        if (active) setDestinations([]);
      });

    return () => {
      active = false;
    };
  }, [mode]);

  useEffect(() => {
    let active = true;

    listApi.getExEndOptions()
      .then((options) => {
        if (active) setExEnds(options);
      })
      .catch(() => {
        if (active) setExEnds([]);
      });

    listApi.getBrands()
      .then((groups: any[]) => {
        if (!active) return;

        const activeType = modeToSearchType(mode);
        setBrands(groups.find((group) => group.type === activeType)?.options ?? []);
      })
      .catch(() => {
        if (active) setBrands([]);
      });

    return () => {
      active = false;
    };
  }, [mode]);

  const changeMode = (nextMode: TravelMode) => {
    if (nextMode === mode) return;

    setDraft((current) => ({
      ...current,
      keyword: "",
      locationId: null,
      brands: [],
      exEnd: []
    }));
    setKeywordText("");
    setOpen(null);
    onModeChange(nextMode);
  };

  const search = () => {
    const query = toQueryString({ ...draft, type: modeToSearchType(mode), sort: "relevance" });
    navigate(`/web/listing${query ? `?${query}` : ""}`);
  };

  const destinationGroups = useMemo<PickerGroup[]>(
    () => {
      const groups = (destinations as any[]) || [];
      const locationOptions = ((groups.find((group) => group.label === "Destinations")?.options || []) as PickerOption[])
        .map((option) => ({ value: option.value, label: option.label }));
      const cityOptions = ((groups.find((group) => group.label === "Cities")?.options || []) as PickerOption[])
        .map((option) => ({ value: option.value, label: option.label }));
      const itineraryOptions = ((groups.find((group) => group.label === "Itineraries")?.options || []) as PickerOption[])
        .map((option) => ({ value: option.value, label: option.label }));
      const nextGroups: PickerGroup[] = [
        {
          heading: "Popular Destinations",
          options: locationOptions.filter((option) => POPULAR.includes(String(option.label))),
        },
        {
          heading: "All Destinations",
          options: locationOptions.filter((option) => !POPULAR.includes(String(option.label))),
        },
      ];

      if (cityOptions.length) nextGroups.push({ heading: "Cities", options: cityOptions });
      if (itineraryOptions.length) nextGroups.push({ heading: "Itineraries", options: itineraryOptions });

      return nextGroups.filter((group) => group.options.length > 0);
    },
    [destinations]
  );

  const departureGroups = useMemo<PickerGroup[]>(
    () => [{ options: ((exEnds as any[]) || []).map(({ value, label }) => ({ value, label })) }],
    [exEnds]
  );

  const brandLabel = (value: string) => brands.find((brand) => brand.value === value)?.label || value;
  const brandTags = () => {
    if (!draft.brands.length) return null;
    const [first, ...rest] = draft.brands;
    return (
      <span className="v2-search-field__tags">
        <span className="v2-search-field__tag">{brandLabel(first)}</span>
        {rest.length > 0 && <span className="v2-search-field__tag">+ {rest.length} More</span>}
      </span>
    );
  };

  const dropdown = (field: OpenField, content: React.ReactNode, children: React.ReactNode) => {
    if (isMobile) return <>{children}</>;

    return (
      <Popover
        open={open === field}
        onOpenChange={(next) => setOpen(next ? field : null)}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        content={content}
        rootClassName="v2-field-dropdown"
      >
        {children}
      </Popover>
    );
  };

  const openSheet = (field: SheetField) => {
    if (!isMobile) return;
    setOpen(null);
    setSheet(field);
  };

  const departureLabel = labelForValue(departureGroups, draft.exEnd[0] || "");

  return (
    <section id="hero-v2">
      <div className="hero-v2__overlay">
        <div className="hero-v2__copy">
          <h1>Travel Further. Experience More.</h1>
          <p>Exclusive tours, cruises and holidays with Malaysia's trusted travel specialist.</p>
        </div>

        <div className="hero-v2__toggle">
          <button
            className={mode === "tour" ? "is-active" : ""}
            onClick={() => changeMode("tour")}
            type="button"
          >
            <img src={`${ICONS}/icon-toggle-tour.png`} alt="" aria-hidden="true" /> Tour
          </button>
          <button
            className={mode === "cruise" ? "is-active" : ""}
            onClick={() => changeMode("cruise")}
            type="button"
          >
            <img src={`${ICONS}/icon-toggle-cruise.png`} alt="" aria-hidden="true" /> Cruise
          </button>
        </div>

        <div className="hero-v2__search">
          {dropdown(
            "keyword",
            <div className="v2-field-dropdown__list">
              <PickerList
                groups={destinationGroups}
                rowIcon={<PinIcon size={16} />}
                value={draft.keyword}
                query={keywordText}
                onSelect={(option) => {
                  setDraft({ ...draft, keyword: option.value, locationId: null });
                  setKeywordText(option.label);
                  setOpen(null);
                }}
              />
            </div>,
            <div className="hero-v2__field" onClick={() => openSheet("destination")}>
              <img src={`${ICONS}/icon-search-location.png`} alt="" aria-hidden="true" />
              <input
                className="hero-v2__input"
                value={keywordText}
                placeholder="Where would you like to go?"
                readOnly={isMobile}
                onFocus={(event) => {
                  // Desktop opening is left to the Popover's own click trigger:
                  // opening here too made the click toggle it straight back
                  // shut, freezing the list at whatever it showed on open.
                  if (isMobile) {
                    event.currentTarget.blur();
                    openSheet("destination");
                  }
                }}
                onChange={(event) => {
                  if (isMobile) return;
                  setKeywordText(event.target.value);
                  setDraft({ ...draft, keyword: event.target.value, locationId: null });
                  setOpen("keyword");
                }}
              />
            </div>
          )}
          {mode !== "cruise" && (
            dropdown(
              "exEnd",
              <div className="v2-field-dropdown__list">
                <PickerList
                  groups={departureGroups}
                  rowIcon={<Icon path={mdiAirplane} size="16px" />}
                  value={draft.exEnd[0] || ""}
                  onSelect={(option) => {
                    setDraft({ ...draft, exEnd: [option.value] });
                    setOpen(null);
                  }}
                />
              </div>,
              <button type="button" className="hero-v2__field hero-v2__field--depart" onClick={() => openSheet("departure")}>
                <img src={`${ICONS}/icon-search-city.png`} alt="" aria-hidden="true" />
                <span className={`hero-v2__value ${departureLabel ? "" : "is-empty"}`}>
                  {departureLabel || "Departure City"}
                </span>
              </button>
            )
          )}
          {dropdown(
            "date",
            <div className="v2-field-dropdown__months">
              <MonthGrid
                year={dateYear}
                onYearChange={setDateYear}
                month={draft.date && dayjs(draft.date).year() === dateYear ? dayjs(draft.date).month() : null}
                onMonthChange={(month) => {
                  setDraft({
                    ...draft,
                    date: month === null ? null : dayjs().year(dateYear).month(month).format("YYYY-MM-01"),
                  });
                  if (month !== null) setOpen(null);
                }}
              />
            </div>,
            <button type="button" className="hero-v2__field" onClick={() => openSheet("date")}>
              <img src={`${ICONS}/icon-search-date.png`} alt="" aria-hidden="true" />
              <span className={`hero-v2__value ${draft.date ? "" : "is-empty"}`}>
                {draft.date ? dayjs(draft.date).format(monthFormat) : "Travel Date"}
              </span>
            </button>
          )}
          {dropdown(
            "brands",
            <BrandDropdown
              brands={brands}
              value={draft.brands}
              onCancel={() => setOpen(null)}
              onApply={(next) => {
                setDraft({ ...draft, brands: next });
                setOpen(null);
              }}
            />,
            <button type="button" className="hero-v2__field hero-v2__field--brands" onClick={() => openSheet("brands")}>
              <img src={`${ICONS}/icon-search-brand.png`} alt="" aria-hidden="true" />
              {brandTags() || <span className="hero-v2__value is-empty">Brand</span>}
            </button>
          )}
          <Button type="primary" className="hero-v2__search-btn" onClick={search}>
            Search Holidays
          </Button>
        </div>
      </div>

      <OptionPickerSheet
        open={sheet === "destination"}
        onClose={() => setSheet(null)}
        title="To Where?"
        placeholder="Where would you like to go?"
        fieldIcon={<PinIcon size={20} />}
        rowIcon={<PinIcon size={16} />}
        groups={destinationGroups}
        value={draft.keyword}
        allowFreeText
        onSelect={(option) => {
          setDraft({ ...draft, keyword: option.value, locationId: null });
          setKeywordText(option.label);
          setSheet(null);
        }}
      />

      {mode !== "cruise" && (
        <OptionPickerSheet
          open={sheet === "departure"}
          onClose={() => setSheet(null)}
          title="Departure From"
          placeholder="Departure From"
          fieldIcon={<HomeIcon size={20} />}
          rowIcon={<Icon path={mdiAirplane} size="16px" />}
          groups={departureGroups}
          value={draft.exEnd[0] || ""}
          onSelect={(option) => {
            setDraft({ ...draft, exEnd: [option.value] });
            setSheet(null);
          }}
        />
      )}

      <MonthPickerSheet
        open={sheet === "date"}
        onClose={() => setSheet(null)}
        value={draft.date}
        onApply={(date) => setDraft({ ...draft, date })}
      />

      <BrandPickerSheet
        open={sheet === "brands"}
        onClose={() => setSheet(null)}
        brands={brands}
        value={draft.brands}
        onApply={(next) => setDraft({ ...draft, brands: next })}
      />
    </section>
  );
}
